import React from 'react';
import { injectIntl } from 'react-intl';
import ReactEcharts from 'echarts-for-react';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import MySlider from '../../../../components/MySlider/MySlider';
import EditIconButton from '../../../../components/EditIconButton';
import MoreInfoTrigger from '../../../../components/MoreInfoDialog/MoreInfoTrigger/MoreInfoTrigger';
import {
  slidingToggleButtonNoBorder,
  slidingToggleGroupNoBorder
} from '../../../../components/SlidingToggleButton/noBorderStyles';
import SavingRatioGauge from '../SavingRatioGauge/SavingRatioGauge';
import { decimalToString, formatNumber } from '../../../../utils/formatNumber';
import './SavingTabV2.scss';

const AccumulateSlider = withStyles(MySlider, {
  root: {
    color: '#13ba65'
  }
});

const RetireSlider = withStyles(MySlider, {
  root: {
    color: '#df933b'
  }
});

const useSwitchStyles = makeStyles()({
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '8px !important',
    padding: 6,
    zIndex: 0,
    ...slidingToggleGroupNoBorder
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '8px !important',
    padding: '0 20px',
    fontWeight: 'normal',
    fontSize: 14,
    height: 36,
    minWidth: 90,
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    left: 6,
    width: 'calc(50% - 6px)',
    backgroundColor: '#3966f8',
    borderRadius: 8,
    boxShadow: '0 4px 10px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1
  }
});

export const SavingUiSwitch = injectIntl(({ intl, version, onChange }) => {
  const { classes } = useSwitchStyles();
  const index = version === 'v2' ? 1 : 0;

  return (
    <div className="saving-v2-switch">
      <ToggleButtonGroup
        value={version}
        exclusive
        onChange={(event, next) => {
          if (next) onChange(next);
        }}
        aria-label={intl.formatMessage({ id: 'analysis-saving-ui-label' })}
        classes={{ root: classes.toggleGroup }}
      >
        <div className={classes.slidingIndicator} style={{ transform: `translateX(${index * 100}%)` }} />
        <ToggleButton value="v1" classes={{ root: classes.toggleButton, selected: classes.selected }}>
          {intl.formatMessage({ id: 'analysis-saving-ui-classic' })}
        </ToggleButton>
        <ToggleButton value="v2" classes={{ root: classes.toggleButton, selected: classes.selected }}>
          {intl.formatMessage({ id: 'analysis-saving-ui-new' })}
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
});

const SliderRow = ({ label, value, unit, age, hint, children }) => (
  <div className="saving-v2__slider">
    <div className="saving-v2__slider-head">
      <span className="saving-v2__slider-label">{label}</span>
      <span className="saving-v2__value">
        {age}
        <span className="saving-v2__chip">
          <strong>{value}</strong>
          {unit ? <span>{unit}</span> : null}
        </span>
      </span>
    </div>
    {hint ? <div className="saving-v2__hint">{hint}</div> : null}
    {children}
  </div>
);

const CHART_AREAS = {
  principal: { color: '#3888f8', id: 'total-principal' },
  future: { color: '#3ffff8', id: 'Future Value' },
  asset: { color: '#c18ff0', id: 'Current Asset' },
  contribution: { color: '#2167ad', id: 'Contribution Period' },
  spending: { color: '#FFA600', id: 'Spending Period' }
};

const ChartKey = ({ intl, items }) => (
  <ul className="saving-v2__key">
    {items.map(item => (
      <li key={item.id}>
        <span className="saving-v2__key-swatch" style={{ background: item.color }} />
        {intl.formatMessage({ id: item.id })}
      </li>
    ))}
  </ul>
);

const SavingTabV2 = ({
  currency,
  showOverview,
  showAge,
  age,
  savingScore,
  savingTotalCoverage,
  savingTerms,
  showAccumulationValueAt,
  returnRate,
  inflationRate,
  monthlyIncome,
  currentAsset,
  principalValue,
  futureValueSecond,
  retireAfter,
  retirementTerms,
  retirementValueAtRetireAfter,
  retirementDisplayValue,
  retirementIncomeLevel,
  savingCompareChartOptions,
  retireChartOptions,
  yearMax,
  returnMax,
  onBeforeSlide,
  onSavingTerms,
  onHoldingPeriod,
  onReturnRate,
  onRetireAfter,
  onRetirementTerms,
  onEditFuture,
  onEditRetirement,
  intl
}) => {
  const currencyLabel = intl.formatMessage({ id: currency });
  const assetValue = Number(currentAsset) || 0;
  const principalAmount = Number(principalValue) || 0;
  const showAssetKey = assetValue > 0 && (principalAmount <= 0 || assetValue >= principalAmount * 0.02);
  const accumulateKey = [
    CHART_AREAS.principal,
    CHART_AREAS.future,
    showAssetKey ? CHART_AREAS.asset : null,
    showAccumulationValueAt > savingTerms ? CHART_AREAS.contribution : null
  ].filter(Boolean);
  const retireKey = [
    CHART_AREAS.principal,
    CHART_AREAS.future,
    showAssetKey ? CHART_AREAS.asset : null,
    retireAfter > savingTerms ? CHART_AREAS.contribution : null,
    CHART_AREAS.spending
  ].filter(Boolean);
  const holdingYears = showAccumulationValueAt - savingTerms;
  const principal = Number(principalValue) || 0;
  const future = Number(futureValueSecond) || 0;
  const delta = future - principal;
  const isLoss = delta < 0;
  const tone = isLoss ? 'loss' : 'gain';
  const percent = principal > 0 ? (Math.abs(delta) / principal) * 100 : null;
  const signedPercent = percent == null ? null : `${isLoss ? '\u2212' : '+'}${percent.toFixed(1)}%`;
  const income = Number(monthlyIncome) || 0;
  const incomeMonths = isLoss && income > 0 ? Math.abs(delta) / income : null;
  const incomeMonthsLabel =
    incomeMonths == null
      ? null
      : (() => {
          const rounded = Math.round(incomeMonths * 10) / 10;
          if (rounded < 0.1) return '< 0.1';
          return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
        })();
  const PnlIcon = isLoss ? TrendingDownIcon : TrendingUpIcon;

  const ageText = plus =>
    showAge ? (
      <span className="saving-v2__age">({intl.formatMessage({ id: 'age-value' }, { age: age + (plus || 0) })})</span>
    ) : null;

  return (
    <div className="saving-v2">
      {showOverview && (
        <div className="saving-v2__overview">
          <section className="saving-v2__card analysis-score">
            <MoreInfoTrigger
              titleIntlId="Saving Ratio"
              contentIntlId="moreInfo_savingScore"
              className="analysis-moreinfo"
            />
            <h3 className="saving-v2__title">{intl.formatMessage({ id: 'Saving Ratio' })}</h3>
            <SavingRatioGauge score={savingScore} />
          </section>

          <section className="saving-v2__card saving-v2__monthly">
            <h3 className="saving-v2__title saving-v2__title--flush">
              {intl.formatMessage({ id: 'Total Monthly Saving' })}
            </h3>
            <div className="saving-v2__monthly-value">
              <span className="saving-v2__currency">{currencyLabel}</span>
              <strong>{decimalToString(savingTotalCoverage)}</strong>
            </div>
          </section>
        </div>
      )}

      <section className="saving-v2__card saving-v2__card--accumulate analysis-accumulation">
        <EditIconButton handleClick={onEditFuture} />
        <h3 className="saving-v2__title">{intl.formatMessage({ id: 'Accumulation' })}</h3>

        <SliderRow
          label={intl.formatMessage({ id: 'Saving Periods' })}
          value={savingTerms}
          unit={intl.formatMessage({ id: 'Yrs' })}
          age={ageText(savingTerms)}
        >
          <AccumulateSlider
            min={1}
            max={yearMax}
            value={savingTerms}
            onChange={onBeforeSlide}
            onChangeCommitted={onSavingTerms}
            stopPropagation={true}
          />
        </SliderRow>

        <SliderRow
          label={intl.formatMessage({ id: 'extra-holding-period' })}
          value={holdingYears}
          unit={intl.formatMessage({ id: 'Yrs' })}
          age={ageText(showAccumulationValueAt)}
        >
          <AccumulateSlider
            min={0}
            max={60}
            value={holdingYears}
            onChange={onBeforeSlide}
            onChangeCommitted={onHoldingPeriod}
            stopPropagation={true}
          />
        </SliderRow>

        <SliderRow
          label={intl.formatMessage({ id: 'with return' })}
          value={`${returnRate}%`}
          unit=""
          hint={
            <span>
              {intl.formatMessage({ id: 'Inflation' })} {inflationRate}%
            </span>
          }
        >
          <AccumulateSlider
            min={0}
            max={returnMax}
            step={0.1}
            value={returnRate}
            onChange={onBeforeSlide}
            onChangeCommitted={onReturnRate}
            stopPropagation={true}
          />
        </SliderRow>

        <div className="saving-v2__metrics">
          <div className="saving-v2__metric">
            <span className="saving-v2__metric-label">{intl.formatMessage({ id: 'total-principal' })}</span>
            <div className="saving-v2__metric-figure">
              <span className="saving-v2__metric-currency">{currencyLabel}</span>
              <strong className="saving-v2__metric-value">{formatNumber(principalValue, intl)}</strong>
            </div>
          </div>
          <div className={'saving-v2__metric saving-v2__metric--' + tone}>
            <span className="saving-v2__metric-label">
              {intl.formatMessage({ id: 'Its value at' })} {savingTerms}+{holdingYears}{' '}
              {intl.formatMessage({ id: 'Yrs' })}
              {ageText(showAccumulationValueAt)}
            </span>
            <div className="saving-v2__metric-figure">
              <span className="saving-v2__metric-currency">{currencyLabel}</span>
              <strong className="saving-v2__metric-value">{formatNumber(futureValueSecond, intl)}</strong>
            </div>
          </div>
        </div>

        <div className={'saving-v2__pnl saving-v2__pnl--' + tone} aria-live="polite">
          <span className="saving-v2__pnl-icon" aria-hidden="true">
            <PnlIcon />
          </span>
          <div className="saving-v2__pnl-copy">
            <div className="saving-v2__pnl-amount">
              <span className="saving-v2__pnl-currency">{currencyLabel}</span>
              <span>
                {isLoss ? '\u2212' : '+'}
                {formatNumber(Math.abs(delta), intl)}
              </span>
            </div>
            {incomeMonthsLabel ? (
              <div className="saving-v2__pnl-note">
                {intl.formatMessage({ id: 'analysis-saving-loss-months' }, { months: incomeMonthsLabel })}
              </div>
            ) : null}
          </div>
          <div className="saving-v2__pnl-kicker">
            {isLoss ? intl.formatMessage({ id: 'analysis-loss' }) : intl.formatMessage({ id: 'Earn' })}
            {signedPercent ? <span className="saving-v2__pnl-percent">{signedPercent}</span> : null}
          </div>
        </div>

        <div className="saving-v2__chart">
          <ReactEcharts option={savingCompareChartOptions} notMerge={true} />
          <ChartKey intl={intl} items={accumulateKey} />
        </div>
      </section>

      <section className="saving-v2__card saving-v2__card--retire analysis-retirement-usage">
        <EditIconButton handleClick={onEditRetirement} />
        <h3 className="saving-v2__title">{intl.formatMessage({ id: 'Retirement' })}</h3>

        <SliderRow
          label={intl.formatMessage({ id: 'After' })}
          value={retireAfter}
          unit={intl.formatMessage({ id: 'Yrs' })}
          age={ageText(retireAfter)}
        >
          <RetireSlider
            min={0}
            max={yearMax}
            value={retireAfter}
            onChange={onBeforeSlide}
            onChangeCommitted={onRetireAfter}
            stopPropagation={true}
          />
        </SliderRow>

        <div className="saving-v2__retire-result">
          <div className="saving-v2__retire-block">
            <span className="saving-v2__metric-label">{intl.formatMessage({ id: 'you would have' })}</span>
            <div className="saving-v2__retire-amount">
              <span>{currencyLabel}</span>
              <strong>{formatNumber(retirementValueAtRetireAfter, intl)}</strong>
            </div>
          </div>
          <div className="saving-v2__retire-block saving-v2__retire-block--income">
            <span className="saving-v2__metric-label">
              {intl.formatMessage({ id: 'It can provide monthly income' })}
            </span>
            <div className="saving-v2__retire-income">
              <div className="saving-v2__retire-amount">
                <span>{currencyLabel}</span>
                <strong>{retirementDisplayValue}</strong>
              </div>
              <img className="saving-v2__level" alt="" {...retirementIncomeLevel} />
            </div>
          </div>
        </div>

        <SliderRow
          label={intl.formatMessage({ id: 'to support your retirement life for' })}
          value={retirementTerms}
          unit={intl.formatMessage({ id: 'years' })}
          age={ageText(retireAfter + retirementTerms)}
        >
          <RetireSlider
            min={1}
            max={yearMax}
            value={retirementTerms}
            onChange={onBeforeSlide}
            onChangeCommitted={onRetirementTerms}
            stopPropagation={true}
          />
        </SliderRow>

        <div className="saving-v2__chart">
          <ReactEcharts option={retireChartOptions} notMerge={true} />
          <ChartKey intl={intl} items={retireKey} />
        </div>
      </section>
    </div>
  );
};

export default injectIntl(SavingTabV2);
